import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import {
    FastTrackApiRoutes,
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ValidateUserForgotPasswordCodeRequest,
    ValidateUserForgotPasswordCodeService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    forLifeOf,
    LifecycleAware,
    Masks,
    FeatureFlagService,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { LoginPageStep, LoginService } from '../login.service';

@LifecycleAware()
@Component({
    selector: 'forgot-password-verification',
    styleUrls: ['./login-step.scss'],
    template: `
        <h1 i18n>Reset Your Password</h1>

        <form class="login-step" [formGroup]="formGroup" (ngSubmit)="resetPassword()">
            <p class="error" *ngIf="errorMessage">
                {{ errorMessage }}
            </p>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'">Email Address</p>
            <lsq-input
                [style.marginBottom]="'2rem'"
                name="emailAddress"
                type="email"
                [basicLabel]="false"
                formControlName="emailAddress"
                [group]="formGroup"
                [autoFocus]="true"
            >
            </lsq-input>

            <p class="body" [style.marginBottom]="'2rem'" i18n>
                Check your email for a 6-digit verification code.
            </p>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'" i18n>Verification Code</p>
            <lsq-input
                [style.marginBottom]="'1rem'"
                name="verificationCode"
                [basicLabel]="false"
                formControlName="verificationCode"
                [group]="formGroup"
                [maxLength]="6"
                [mask]="digitsOnlyMask"
            >
            </lsq-input>

            <p class="body" [style.marginBottom]="'2rem'" i18n>
                Passwords must be at least 8 characters, contain one number, one capital letter, one
                lowercase letter, and one special character.
            </p>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'">New Password</p>
            <lsq-input
                [style.marginBottom]="'2rem'"
                type="password"
                [basicLabel]="false"
                name="password"
                formControlName="password"
                [group]="formGroup"
            >
            </lsq-input>

            <!-- TODO(PDEV-223): Remove label in favor of input's own label -->
            <p class="label" [style.marginBottom]="'-0.5rem'">Confirm Password</p>
            <lsq-input
                [style.marginBottom]="'3rem'"
                type="password"
                [basicLabel]="false"
                name="confirmPassword"
                formControlName="confirmPassword"
                [group]="formGroup"
            >
            </lsq-input>

            <lsq-button
                type="submit"
                size="block"
                [submitting]="isSubmitting"
                [style.marginBottom]="'2rem'"
            >
                Reset my password
            </lsq-button>

            <lsq-button appearance="transparent" size="block" (buttonClick)="backToLogin()">
                Go back
            </lsq-button>
        </form>
    `
})
export class ForgotPasswordVerificationComponent {
    public isSubmitting = false;
    public errorMessage: string | null | undefined;

    public readonly digitsOnlyMask = Masks.DigitsOnlyMask(6);

    public forgotPasswordEnhancementIsOn$ = this._featureFlagService.isOn(
        FeatureFlags.ForgotPasswordEnhancement
    );

    private readonly _passwordsMustMatchCopy = this._i18n({
        id: 'loginPasswordAndConfirmMustMatch',
        value: `"Password" and "Confirm Password" must match.`
    });

    public formGroup = this._formBuilder.group({
        emailAddress: ['', [Validators.required, LsqValidators.email]],
        verificationCode: ['', [Validators.required, LsqValidators.exactLength(6)]],
        password: [
            '',
            [
                Validators.required,
                LsqValidators.strictPassword,
                LsqValidators.matchValueSource(
                    () => this.formGroup?.get('confirmPassword'),
                    this._passwordsMustMatchCopy
                )
            ]
        ],
        confirmPassword: [
            '',
            [
                Validators.required,
                LsqValidators.matchValueConfirm(
                    () => this.formGroup?.get('password'),
                    this._passwordsMustMatchCopy
                )
            ]
        ]
    });

    constructor(
        private _i18n: I18n,
        private _formBuilder: FormBuilder,
        private _loginService: LoginService,
        private _alertService: AlertService,
        private _featureFlagService: FeatureFlagService,
        private _validateUserForgotPasswordCodeService: ValidateUserForgotPasswordCodeService,
        private _errorToastBlacklistService: ErrorToastBlacklistService
    ) {
        this._loginService.routeParams$.pipe(forLifeOf(this)).subscribe((params) => {
            if (params.get(FastTrackRouteQueryParams.VerificationCode)) {
                this.formGroup
                    .get('verificationCode')
                    .setValue(params.get(FastTrackRouteQueryParams.VerificationCode));
            }
        });

        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ValidateUserForgotPasswordCode}`,
            ResponseCode.InvalidRequest
        );
    }

    public ngOnDestroy(): void {}

    public async resetPassword(): Promise<void> {
        this.formGroup.markAllAsTouched();
        this.errorMessage = null;

        if (this.formGroup.valid) {
            this.isSubmitting = true;
            const emailAddress = this.formGroup.get('emailAddress').value;
            const password = this.formGroup.get('password').value;

            if (this.forgotPasswordEnhancementIsOn$) {
                try {
                    const validateForgotPasswordResult = await this._validateUserForgotPasswordCodeService.executeOnce(
                        new ValidateUserForgotPasswordCodeRequest({
                            emailAddress: emailAddress,
                            verificationCode: this.formGroup.get('verificationCode').value,
                            password: password,
                            confirmPassword: this.formGroup.get('confirmPassword').value
                        })
                    );

                    if (validateForgotPasswordResult.code == ResponseCode.Success) {
                        this._loginService.loginWithEmailAndPassword(emailAddress, password);
                    }
                } catch {
                    this.errorMessage = 'Invalid verification code provided, please try again.';
                }
            } else {
                const result = await this._loginService.completeForgotPasswordReset(
                    emailAddress,
                    this.formGroup.get('verificationCode').value,
                    this.formGroup.get('password').value
                );

                if (result.authorized) {
                    this._alertService.success('Your password has been successfully updated.');
                    this._loginService.setLoginPageStep(LoginPageStep.SignIn);
                } else {
                    this.errorMessage = result.message;
                }
            }
        }
        this.isSubmitting = false;
    }

    public backToLogin(): void {
        this._loginService.setLoginPageStep(LoginPageStep.SignIn);
    }
}
